import type { ReactNode } from 'react';

import { AppLink } from '../link.js';
import { RECORD_PAGES, sectionHref } from './project-sections.js';

/**
 * The record page, as a frame.
 *
 * WHY A PAGE AND NOT THE DRAWER IT REPLACES
 * -----------------------------------------
 * The drawer was the right answer to "show me this row without losing the
 * register", and for most records it still is. It stops being the right answer
 * when the record is made of LISTS. A material carries a procurement history, a
 * file trail and the approval it waited on; three lists in a 480px panel is
 * three scrolling boxes inside a scrolling box, and the reader loses which one
 * they are in. A page has the width to lay them out side by side, and — the
 * part the drawer could never do — it has an ADDRESS, so the material a buyer
 * is chasing can be sent to the person who has to answer for it.
 *
 * The decision of which records get one is `RECORD_PAGES` in
 * `project-sections.ts`, and it is a decision rather than a to-do list. The rule
 * is the same one that decided drawer-versus-inline, one level up: a page is for
 * a record that has a life of its own. A folder does not.
 *
 * WHAT THIS FILE KNOWS: nothing about a domain. Same rule as `register-kit.tsx`
 * — no API import, no status vocabulary, no money. A frame that knew what a
 * material was would be a frame the drawings register could not use.
 *
 * THE LAYOUT IS THE OVERVIEW'S, DELIBERATELY REUSED
 * -------------------------------------------------
 * `.split` / `.split-main` / `.split-rail` already carry the two-column grid,
 * the 1100px fold and the `minmax(0, 1fr)` track that stops a table blowing the
 * page out sideways (see `shell.css`, and §6a of the handover for the three
 * faults that cost). Inventing a second two-column grid here would be inventing
 * a second set of those bugs.
 *
 * The columns are split the same way the overview splits them, for the same
 * reason: the MAIN column is what the record is DOING — its history, its files,
 * the records hanging off it — and the rail is what it IS. Doing changes daily
 * and is what somebody came for; being is reference.
 */

export interface RecordPageProps {
  readonly projectId: string;
  /** The key in `RECORD_PAGES`. Drives the link back and nothing else. */
  readonly register: string;
  /**
   * The record's own reference — `MAT-0007`, `DWG-102`. Set in the mono face.
   *
   * OPTIONAL, because not every record has one. A task is named by its title
   * and nothing else, and putting a task's title in the mono face would say
   * "this is a code" about a sentence somebody typed.
   */
  readonly reference?: string;
  /** What it is, in words. The reference alone is not a title. */
  readonly title: string;
  /**
   * Where the record has got to, in WORDS.
   *
   * A word rather than a colour, and it is not optional: the design system's
   * rule is that status is never said by colour alone, and a record page whose
   * only statement of state was a tinted pill would be exactly that.
   */
  readonly status: string;
  /** A class for the status, so it can also be marked. Never the only signal. */
  readonly statusClass?: string;
  /** One line under the title — dates, an owner, a supplier. */
  readonly meta?: ReactNode;
  /**
   * The action bar. `RowActions` from the register kit goes here unchanged.
   *
   * The SAME control as the row's, on purpose. It is the same set of moves with
   * the same one-forward-move rule, its keyboard model is already right, and a
   * second menu written for pages would be a second menu to get wrong. What the
   * page adds is room: the bar sits beside the title instead of at the end of a
   * six-column row.
   */
  readonly actions?: ReactNode;
  /** The rail: what the record IS. A `<dl className="detail-grid">`. */
  readonly properties: ReactNode;
  /** The main column: what the record is DOING. */
  readonly children: ReactNode;
}

export function RecordPage({
  projectId,
  register,
  reference,
  title,
  status,
  statusClass,
  meta,
  actions,
  properties,
  children,
}: RecordPageProps): JSX.Element {
  return (
    <>
      <RecordBack projectId={projectId} register={register} />

      {/*
        THE STATUS COMES BEFORE THE ACTIONS IN THE SOURCE, and that is the
        reason this is two boxes rather than a title row with a line under it.
        Read out in order, "MAT-0002 · Ready to order" then the moves is a record
        being described and then acted on; the other way round offers somebody a
        move before telling them where the record has got to. The layout is
        unaffected — the actions still sit top-right, because the flex row is
        this element and not the title.
      */}
      <div className="record-head">
        <div className="record-head-main">
          <h3 className="record-title">
            {reference && (
              <>
                <span className="mono">{reference}</span>{' '}
              </>
            )}
            <span>{title}</span>
          </h3>

          <p className="record-meta">
            <span className={statusClass ? `record-status ${statusClass}` : 'record-status'}>
              {status}
            </span>
            {meta && <span className="record-meta-rest">{meta}</span>}
          </p>
        </div>

        {actions && <div className="record-actions">{actions}</div>}
      </div>

      <div className="split record-body">
        <div className="split-main">{children}</div>
        <aside className="split-rail">
          <div className="register">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Details</h4>
            </div>
            {properties}
          </div>
        </aside>
      </div>
    </>
  );
}

/**
 * The link back to the register a record belongs to.
 *
 * BACK TO THE REGISTER, not back to the project. The register is where the
 * reader came from and where the next material is; the project head above this
 * already names the project, and the tab strip is still the way to anywhere
 * else. A second "← Projects" here would be a link to the one place nobody is
 * going next.
 *
 * Exported because a record page's OTHER states need it too — a register still
 * loading, a register this role may not read, an id that names nothing. Every
 * one of those has to leave a way out, and it is the same way out.
 */
export function RecordBack({
  projectId,
  register,
}: {
  readonly projectId: string;
  readonly register: string;
}): JSX.Element {
  const home = RECORD_PAGES[register];
  return (
    <p className="breadcrumb record-back">
      <AppLink to={sectionHref(projectId, home?.section ?? 'overview')}>
        ← {home?.label ?? 'Back'}
      </AppLink>
    </p>
  );
}

/**
 * An id that names nothing in the register.
 *
 * A FOURTH SENTENCE, and it has to be its own. Loading, empty, not-permitted and
 * failed are already four distinct states in this product because conflating any
 * two of them tells somebody something false — and a record that has been
 * deleted, or a link pasted from another project, is a fifth thing again. It is
 * not "you may not see this" (that would be a lie to somebody who may) and it is
 * not "nothing here yet" (the register is full).
 *
 * It names the one action its reader can take, which is to go and look at the
 * register — the rule this product has shipped wrong six times.
 */
export function RecordMissing({
  projectId,
  register,
}: {
  readonly projectId: string;
  readonly register: string;
}): JSX.Element {
  const home = RECORD_PAGES[register];
  const noun = home?.noun ?? 'record';

  return (
    <>
      <RecordBack projectId={projectId} register={register} />
      <div className="empty">
        <p>
          <strong>That {noun} is not here.</strong>
        </p>
        <p>
          It may have been deleted, or the link may point at another project.{' '}
          <AppLink to={sectionHref(projectId, home?.section ?? 'overview')}>
            Open the {home?.plural ?? 'register'}
          </AppLink>{' '}
          to see what is on this one.
        </p>
      </div>
    </>
  );
}
